import { ExclamationCircleFilled } from '@ant-design/icons'
import { faPills, faPlus, faTrash } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  Modal as AntModal,
  Button,
  Card,
  Col,
  Modal,
  Row,
  Select,
  Space,
  Typography,
  DatePicker as AntDatePicker
} from 'antd'
import dayjs from 'dayjs'
import React, { useState, useEffect, useRef } from 'react'
import DesktopMedicalHistoryNav from './Nav'
import useGeneralMeds from '../../hooks/useGeneralMeds'
import '../style/DesktopGeneralMeds.css'
import DesktopSectionHeader from './DesktopSectionHeader'

const DesktopGeneralMeds = ({ clientForm, data, setFacet, setSaving }) => {
  const {
    medsList,
    nappiData,
    nappiLoading,
    setNappiSearchFilter,
    handleAddMedication,
    handleUpdateMedication,
    handleRemoveMedication,
    handleSubmit,
    showMedicationModal,
    setShowMedicationModal,
    editingMedication,
    setEditingMedication,
    tempMedDates,
    setTempMedDates
  } = useGeneralMeds({ clientForm, setFacet, setSaving })

  // Unsaved changes modal state
  const [showUnsavedModal, setShowUnsavedModal] = useState(false)
  const [pendingNavigation, setPendingNavigation] = useState(null)
  const initialState = useRef(null)

  // Selected medication state for the modal
  const [selectedMedication, setSelectedMedication] = useState(null)

  // Capture initial state when component loads - with delay to ensure state is loaded
  useEffect(() => {
    const timer = setTimeout(() => {
      if (!initialState.current && medsList) {
        initialState.current = {
          medsList: JSON.parse(JSON.stringify(medsList))
        }
        console.log('Initial general medications state captured:', initialState.current)
      }
    }, 300)

    return () => clearTimeout(timer)
  }, [medsList])

  // Clear initial state when component unmounts
  useEffect(() => {
    return () => {
      initialState.current = null
    }
  }, [])

  // Scroll to top when component mounts
  useEffect(() => {
    setTimeout(() => {
      document.documentElement.scrollTop = 0
      document.body.scrollTop = 0
    }, 0)
  }, [])

  // Check if there are unsaved changes
  const hasUnsavedChanges = () => {
    if (!initialState.current) return false

    const hasChanges = JSON.stringify(medsList) !== JSON.stringify(initialState.current.medsList)
    console.log('General medications change detection:', {
      hasChanges,
      initial: initialState.current.medsList,
      current: medsList
    })

    return hasChanges
  }

  // Handle navigation with unsaved changes check
  const handleNavigation = (navigationFunction) => {
    if (hasUnsavedChanges()) {
      setPendingNavigation(() => navigationFunction)
      setShowUnsavedModal(true)
    } else {
      navigationFunction()
    }
  }

  // Update initial state after successful save
  const updateInitialStateAfterSave = () => {
    initialState.current = {
      medsList: JSON.parse(JSON.stringify(medsList))
    }
    console.log('Initial state updated after save:', initialState.current)
  }

  // Wrapper for handleSubmit that also updates initial state
  const handleSaveWithStateUpdate = async () => {
    try {
      await handleSubmit()
      updateInitialStateAfterSave()
    } catch (error) {
      // Error is already handled in handleSubmit
    }
  }

  // Handle saving and then navigating
  const handleSaveAndNavigate = async () => {
    try {
      await handleSubmit()
      updateInitialStateAfterSave()
      setShowUnsavedModal(false)
      if (pendingNavigation) {
        pendingNavigation()
        setPendingNavigation(null)
      }
    } catch (error) {
      // Error is already handled in handleSubmit
    }
  }

  // Handle navigation without saving
  const handleNavigateWithoutSaving = () => {
    setShowUnsavedModal(false)
    if (pendingNavigation) {
      pendingNavigation()
      setPendingNavigation(null)
    }
  }

  // Helper function to convert text to title case
  const toTitleCase = (str) => {
    if (!str) return str
    return str.toLowerCase().replace(/\b\w/g, (l) => l.toUpperCase())
  }

  // Helper function to get display name from selected medication
  const getSelectedMedicationDisplay = () => {
    if (!selectedMedication) return undefined
    try {
      const med = JSON.parse(selectedMedication)
      return toTitleCase(med.name || med.genericName || med.description || 'Unknown Medication')
    } catch {
      return selectedMedication
    }
  }

  const showDeleteMedicationConfirm = (medication, index) => {
    AntModal.confirm({
      title: 'Remove Medication',
      icon: <ExclamationCircleFilled style={{ color: '#FF4D4F' }} />,
      content: (
        <div>
          <p>
            Are you sure you want to remove <strong>{medication.med.name}</strong>?
          </p>
          <p>This action cannot be undone.</p>
        </div>
      ),
      okText: 'Remove',
      okButtonProps: { danger: true },
      cancelText: 'Cancel',
      onOk() {
        handleRemoveMedication(index)
      }
    })
  }

  return (
    <div className='desktop-general-meds-container'>
      <DesktopSectionHeader
        icon={faPills}
        title='General Medications'
        subtitle='Track all medications you are currently taking or have taken in the past'
        gradient='linear-gradient(135deg, #F57C00 0%, #F9A825 100%)'
      />

      <div className='desktop-general-meds-content'>
        {/* Medications List with Add Button */}
        <Row gutter={[24, 24]}>
          {medsList.map((medication, index) => (
            <Col xs={24} sm={12} lg={12} xl={8} key={index}>
              <Card className='desktop-medication-card'>
                <div className='desktop-medication-header'>
                  <div className='desktop-medication-info'>
                    <div className='desktop-med-name-row'>
                      <FontAwesomeIcon icon={faPills} className='desktop-med-icon' />
                      <h4 className='desktop-medication-name'>{toTitleCase(medication.med.name)}</h4>
                    </div>
                    <div className='desktop-medication-dates'>
                      <Typography.Text type='secondary'>
                        <strong>Started:</strong>{' '}
                        {medication.startDate ? dayjs(medication.startDate).format('MMM YYYY') : 'Unknown'}
                      </Typography.Text>
                      <Typography.Text type='secondary'>
                        <strong>Ended:</strong>{' '}
                        {medication.endDate ? dayjs(medication.endDate).format('MMM YYYY') : 'Ongoing'}
                      </Typography.Text>
                    </div>
                  </div>
                  <Button
                    danger
                    type='text'
                    icon={<FontAwesomeIcon icon={faTrash} />}
                    onClick={() => showDeleteMedicationConfirm(medication, index)}
                    className='desktop-remove-medication-btn'
                  />
                </div>
              </Card>
            </Col>
          ))}

          {/* Add New Medication Button - appears to the right of the last medication */}
          {medsList.length > 0 && (
            <Col xs={24} sm={12} lg={12} xl={8}>
              <Card className='desktop-add-medication-card'>
                <Button
                  type='primary'
                  icon={<FontAwesomeIcon icon={faPlus} />}
                  onClick={() => setShowMedicationModal(true)}
                  size='large'
                  className='desktop-add-medication-btn'
                >
                  Add Medication
                </Button>
              </Card>
            </Col>
          )}
        </Row>

        {/* Empty State Message with Add Button */}
        {medsList.length === 0 && (
          <>
            <Card className='desktop-empty-state'>
              <div className='desktop-empty-content'>
                <FontAwesomeIcon icon={faPills} className='desktop-empty-icon' />
                <h3>No Medications Added</h3>
                <p>Click "Add Medication" below to start tracking your current and past medications.</p>
              </div>
            </Card>

            {/* Add New Medication Button - appears below empty state */}
            <Card className='desktop-add-medication-card'>
              <Button
                type='primary'
                icon={<FontAwesomeIcon icon={faPlus} />}
                onClick={() => setShowMedicationModal(true)}
                size='large'
                className='desktop-add-medication-btn'
              >
                Add Medication
              </Button>
            </Card>
          </>
        )}
      </div>

      {/* Add/Edit Medication Modal */}
      <Modal
        title={editingMedication !== null ? 'Edit Medication' : 'Add Medication'}
        open={showMedicationModal}
        onCancel={() => {
          setShowMedicationModal(false)
          setEditingMedication(null)
          setTempMedDates({ startDate: null, endDate: null })
          setSelectedMedication(null)
        }}
        footer={null}
        className='desktop-medication-modal'
      >
        <div className='desktop-modal-content'>
          <div className='desktop-form-field'>
            <label>Medication Name</label>
            <Select
              showSearch
              placeholder='Search for medication...'
              style={{ width: '100%' }}
              loading={nappiLoading}
              onSearch={setNappiSearchFilter}
              filterOption={false}
              notFoundContent={nappiLoading ? 'Searching...' : 'No medications found'}
              value={getSelectedMedicationDisplay()}
              onChange={setSelectedMedication}
            >
              {nappiData && nappiData.length > 0
                ? nappiData.map((med, index) => {
                    const medicationName = med.name || med.genericName || med.description || `Medication ${index + 1}`
                    return (
                      <Select.Option key={index} value={JSON.stringify(med)}>
                        {toTitleCase(medicationName)}
                      </Select.Option>
                    )
                  })
                : null}
            </Select>
          </div>

          <Row gutter={16}>
            <Col span={12}>
              <div className='desktop-form-field'>
                <label>Start Date</label>
                <AntDatePicker
                  placeholder='Select start date'
                  value={tempMedDates.startDate ? dayjs(tempMedDates.startDate) : null}
                  onChange={(date) => {
                    setTempMedDates((prev) => ({
                      ...prev,
                      startDate: date ? date.format('YYYY-MM-DD') : null
                    }))
                  }}
                  disabledDate={(current) => current && current > dayjs().endOf('day')}
                  style={{ width: '100%', height: '40px' }}
                  format='YYYY-MM-DD'
                />
              </div>
            </Col>
            <Col span={12}>
              <div className='desktop-form-field'>
                <label>End Date (Optional)</label>
                <AntDatePicker
                  placeholder='Ongoing'
                  value={tempMedDates.endDate ? dayjs(tempMedDates.endDate) : null}
                  onChange={(date) => {
                    setTempMedDates((prev) => ({
                      ...prev,
                      endDate: date ? date.format('YYYY-MM-DD') : null
                    }))
                  }}
                  disabledDate={(current) => current && current > dayjs().endOf('day')}
                  style={{ width: '100%', height: '40px' }}
                  format='YYYY-MM-DD'
                  allowClear
                />
              </div>
            </Col>
          </Row>

          <div className='desktop-modal-actions'>
            <Space>
              <Button onClick={() => setShowMedicationModal(false)}>Cancel</Button>
              <Button
                type='primary'
                onClick={() => {
                  if (editingMedication !== null) {
                    handleUpdateMedication(editingMedication, tempMedDates)
                  } else {
                    handleAddMedication(selectedMedication, tempMedDates)
                    setSelectedMedication(null)
                  }
                }}
                disabled={
                  editingMedication !== null ? !tempMedDates.startDate : !selectedMedication || !tempMedDates.startDate
                }
              >
                {editingMedication !== null ? 'Update' : 'Add'} Medication
              </Button>
            </Space>
          </div>
        </div>
      </Modal>

      {/* Unsaved Changes Modal */}
      <Modal
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <FontAwesomeIcon icon={faPills} style={{ color: '#ff6b6b' }} />
            <span>Unsaved Changes</span>
          </div>
        }
        open={showUnsavedModal}
        onCancel={() => setShowUnsavedModal(false)}
        footer={null}
        className='desktop-unsaved-changes-modal'
        centered
      >
        <div className='desktop-unsaved-changes-content'>
          <Typography.Text className='desktop-unsaved-changes-message'>
            You have unsaved changes to your medication information. What would you like to do?
          </Typography.Text>

          <div className='desktop-unsaved-changes-actions'>
            <Button type='primary' className='desktop-save-changes-btn' onClick={handleSaveAndNavigate} loading={false}>
              Save Changes
            </Button>
            <Button type='default' className='desktop-discard-changes-btn' onClick={handleNavigateWithoutSaving}>
              Discard Changes
            </Button>
            <Button type='text' className='desktop-cancel-modal-btn' onClick={() => setShowUnsavedModal(false)}>
              Continue Editing
            </Button>
          </div>
        </div>
      </Modal>

      <DesktopMedicalHistoryNav
        prevFunction={() => handleNavigation(() => setFacet(null))}
        nextFunction={handleSaveWithStateUpdate}
        prevText='Back to Overview'
        nextText='Save Medications'
      />
    </div>
  )
}

export default DesktopGeneralMeds
